import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import VehiclesManager from "@/components/VehiclesManager";
import { formatVehiclesForPayload } from "@/lib/vehicles";

const TOWERS = [
    { value: "A", label: "Tower A" },
    { value: "B", label: "Tower B" },
    { value: "C", label: "Tower C" },
    { value: "D", label: "Tower D" },
    { value: "E", label: "Tower E" },
    { value: "F", label: "Tower F" },
];

export default function Onboarding() {
    const { user, refresh } = useAuth();
    const navigate = useNavigate();
    const nameParts = (user?.name || "").trim().split(" ");
    const [firstName, setFirstName] = useState(nameParts[0] || "");
    const [lastName, setLastName] = useState(nameParts.slice(1).join(" ") || "");
    const [role, setRole] = useState("resident");
    const [occupancy, setOccupancy] = useState("owner"); // "owner" | "tenant"
    const [tower, setTower] = useState(user?.tower || "");
    const [flat, setFlat] = useState(user?.flat_no || "");
    const [phone, setPhone] = useState(() => (user?.phone || "").replace(/\D/g, "").slice(-10));
    const [vehicles, setVehicles] = useState([]);
    const [saving, setSaving] = useState(false);

    const handlePhoneChange = (e) => {
        let val = e.target.value.replace(/\D/g, "");
        if (val.length === 12 && val.startsWith("91")) {
            val = val.slice(2);
        } else if (val.length === 11 && val.startsWith("0")) {
            val = val.slice(1);
        }
        setPhone(val.slice(0, 10));
    };

    const submit = async (e) => {
        e.preventDefault();

        const cleanPhone = phone.replace(/\D/g, "");
        if (!cleanPhone || cleanPhone.length !== 10) {
            toast.error("Please enter a valid 10-digit mobile phone number.");
            return;
        }
        if (!/^[6-9]\d{9}$/.test(cleanPhone)) {
            toast.error("Please enter a valid 10-digit Indian mobile number (starting with 6, 7, 8, or 9).");
            return;
        }

        if (role === "resident") {
            if (!tower) {
                toast.error("Please select your Tower / Block.");
                return;
            }
            if (!flat.trim()) {
                toast.error("Please enter your Flat number.");
                return;
            }
        }

        setSaving(true);
        try {
            await api.post("/auth/onboard", {
                name: `${firstName.trim()} ${lastName.trim()}`.trim(),
                first_name: firstName.trim(),
                last_name: lastName.trim(),
                role,
                occupancy: role === "resident" ? occupancy : null,
                tower: role === "resident" ? tower : null,
                flat_no: role === "resident" ? flat.trim() : null,
                phone: cleanPhone,
                vehicles: role === "resident" ? formatVehiclesForPayload(vehicles) : null,
            });
            await refresh();
            toast.success("Profile submitted! Awaiting society admin verification.");
            navigate("/pending-approval", { replace: true });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not save onboarding");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="min-h-screen bg-nest-base">
            <div className="max-w-3xl mx-auto px-6 py-16 lg:py-24" data-testid="onboarding-page">
                <p className="nest-stat-label">Onboarding</p>
                <h1 className="font-display text-4xl lg:text-5xl tracking-tighter mt-2">
                    Tell us about you{firstName ? `, ${firstName}` : ""}.
                </h1>
                <p className="mt-3 text-nest-ink2">Complete your profile to personalize your nest. You can update it later anytime.</p>

                <form onSubmit={submit} className="mt-12 grid gap-10">
                    <div>
                        <p className="nest-stat-label">Your Name</p>
                        <div className="mt-4 grid sm:grid-cols-2 gap-6">
                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wider text-nest-ink3">First Name *</label>
                                <input
                                    data-testid="onboarding-first-name-input"
                                    value={firstName}
                                    onChange={(e) => setFirstName(e.target.value)}
                                    placeholder="e.g., Tilak"
                                    required
                                    className="mt-2 w-full bg-transparent border-b border-nest-line focus:border-nest-clay outline-none py-3 text-lg"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wider text-nest-ink3">Last Name *</label>
                                <input
                                    data-testid="onboarding-last-name-input"
                                    value={lastName}
                                    onChange={(e) => setLastName(e.target.value)}
                                    placeholder="e.g., Kalbande"
                                    required
                                    className="mt-2 w-full bg-transparent border-b border-nest-line focus:border-nest-clay outline-none py-3 text-lg"
                                />
                            </div>
                        </div>
                    </div>

                    <div>
                        <p className="nest-stat-label">Your role</p>
                        <div className="mt-4 grid sm:grid-cols-3 gap-4">
                            {[
                                { v: "resident", label: "Resident", desc: "I live here.", disabled: false },
                                { v: "guard", label: "Guard", desc: "Assigned by society admin.", disabled: true, tag: "Disabled" },
                                { v: "admin", label: "Admin", desc: "Created by society owner.", disabled: true, tag: "Disabled" },
                            ].map((opt) => (
                                <button
                                    type="button"
                                    key={opt.v}
                                    disabled={opt.disabled}
                                    onClick={() => !opt.disabled && setRole(opt.v)}
                                    data-testid={`role-${opt.v}-btn`}
                                    className={`relative text-left p-5 rounded-xl border transition-all ${
                                        opt.disabled
                                            ? "border-nest-line/60 bg-nest-alt/40 opacity-55 cursor-not-allowed"
                                            : role === opt.v
                                            ? "border-nest-clay bg-white shadow-md ring-2 ring-nest-clay/20 cursor-pointer"
                                            : "border-nest-line bg-white/60 hover:border-nest-sand cursor-pointer"
                                    }`}
                                >
                                    {opt.tag && (
                                        <span className="absolute top-3 right-3 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-nest-sand/40 text-nest-ink3 border border-nest-line">
                                            {opt.tag}
                                        </span>
                                    )}
                                    <p className="font-display text-xl">{opt.label}</p>
                                    <p className="text-sm text-nest-ink2 mt-1">{opt.desc}</p>
                                </button>
                            ))}
                        </div>
                    </div>

                    {role === "resident" && (
                        <>
                            <div>
                                <p className="nest-stat-label">Are you the Flat Owner or Tenant?</p>
                                <div className="mt-3 grid grid-cols-2 gap-4 max-w-md">
                                    {[
                                        { id: "owner", label: "Owner", desc: "I own this flat" },
                                        { id: "tenant", label: "Tenant", desc: "I am renting this flat" },
                                    ].map((opt) => (
                                        <button
                                            type="button"
                                            key={opt.id}
                                            onClick={() => setOccupancy(opt.id)}
                                            data-testid={`occupancy-${opt.id}-btn`}
                                            className={`text-left p-4 rounded-xl border transition-all cursor-pointer ${
                                                occupancy === opt.id
                                                    ? "border-nest-clay bg-white shadow-md ring-2 ring-nest-clay/20"
                                                    : "border-nest-line bg-white/60 hover:border-nest-sand"
                                            }`}
                                        >
                                            <p className="font-display font-semibold text-nest-ink text-base">{opt.label}</p>
                                            <p className="text-xs text-nest-ink2 mt-0.5">{opt.desc}</p>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="grid sm:grid-cols-2 gap-6">
                                <div>
                                    <label className="nest-stat-label">Tower / Block *</label>
                                    <div className="relative mt-3">
                                        <select
                                            data-testid="onboarding-tower-input"
                                            value={tower}
                                            onChange={(e) => setTower(e.target.value)}
                                            required
                                            className="w-full bg-transparent border-b border-nest-line focus:border-nest-clay outline-none py-3 text-lg font-medium text-nest-ink appearance-none cursor-pointer pr-8"
                                        >
                                            <option value="" disabled className="text-nest-ink3">Select Tower / Block</option>
                                            {TOWERS.map((t) => (
                                                <option key={t.value} value={t.value} className="bg-white text-nest-ink py-2">
                                                    {t.label}
                                                </option>
                                            ))}
                                        </select>
                                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-1 text-nest-ink3">
                                            <svg className="w-5 h-5 fill-current" viewBox="0 0 20 20">
                                                <path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" fillRule="evenodd" />
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <label className="nest-stat-label">Flat number *</label>
                                    <input
                                        data-testid="onboarding-flat-input"
                                        value={flat}
                                        onChange={(e) => setFlat(e.target.value)}
                                        placeholder="e.g., 601"
                                        required
                                        className="mt-3 w-full bg-transparent border-b border-nest-line focus:border-nest-clay outline-none py-3 text-lg font-medium text-nest-ink"
                                    />
                                </div>
                            </div>
                        </>
                    )}

                    <div>
                        <div className="flex items-center justify-between">
                            <label className="nest-stat-label">Mobile Phone Number *</label>
                            <span className="text-[11px] font-semibold text-nest-clay uppercase tracking-wider">Mandatory</span>
                        </div>
                        <div className="mt-3 flex items-center border-b border-nest-line focus-within:border-nest-clay transition">
                            <span className="text-lg font-bold text-nest-ink3 pr-2 select-none">+91</span>
                            <input
                                data-testid="onboarding-phone-input"
                                type="tel"
                                required
                                value={phone}
                                onChange={handlePhoneChange}
                                placeholder="98765 43210"
                                className="w-full bg-transparent outline-none py-3 text-lg font-medium text-nest-ink tracking-wide placeholder:text-nest-ink3/40"
                                maxLength={10}
                            />
                        </div>
                        <p className="text-xs text-nest-ink3 mt-1.5">Required for visitor gate approval notifications and society security alerts.</p>
                    </div>

                    {role === "resident" && (
                        <div className="bg-white/70 p-5 rounded-2xl border border-nest-line shadow-2xs">
                            <VehiclesManager
                                vehicles={vehicles}
                                onChange={setVehicles}
                                label="Your Vehicles (Cars & Bikes)"
                                helperText="Add all cars and bikes for gate security & parking."
                            />
                        </div>
                    )}

                    <div className="pt-4">
                        <button
                            type="submit"
                            disabled={saving}
                            data-testid="onboarding-submit-btn"
                            className="nest-btn-primary"
                        >
                            {saving ? "Saving…" : "Enter NeighborNest →"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
